import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, ArrowLeft, CheckCircle2, Award, Cog, Clock, Shield } from 'lucide-react';
import { Zap, Settings, Wrench } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const heroRef = useRef(null);
  const subServicesRef = useRef(null);
  const ctaRef = useRef(null);

  // Services data with 4 main categories and their subcategories
  const servicesData = {
    'sheet-laser-cutting': {
      id: 1,
      title: "Sheet Laser Cutting",
      icon: <Zap className="w-12 h-12" />,
      description: "High-precision sheet laser cutting services using advanced PRIDELASER Fiber Laser technology. MS up to 25MM, SS up to 16MM, Copper up to 3MM. ±0.1mm accuracy for industrial applications.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/sheetlaser.png",
      color: "bg-primary",
      subServices: [
        {
          name: "Laser Sheet Cutting",
          description: "BODOR Fiber Laser Cutting Machine with high-power performance for precision metal sheet cutting.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/1.webp",
          specifications: [
            { label: "Machine Make", value: "BODOR" },
            { label: "Working Area", value: "3000*1500 MM" },
            { label: "Machine Type", value: "Fiber Laser" },
            { label: "Gas Used", value: "Nitrogen, Oxygen" },
            { label: "MS Capacity", value: "Up to 16MM" },
            { label: "SS Capacity", value: "Up to 12MM" },
            { label: "Copper Capacity", value: "Up to 3MM" }
          ],
          capabilities: [
            "High-power laser for thick material cutting",
            "Precision cutting with minimal kerf width",
            "Efficient material utilization",
            "Smooth edge finish requiring no secondary processing"
          ],
          applications: ["Heavy industrial parts", "Structural components", "Machine parts", "Custom fabrication", "Sheet metal prototyping"]
        },
        {
          name: "Laser Sheet Cutting",
          description: "PRIDELASER Fiber Laser Cutting Machine with 6000*2000 MM working area for precision metal sheet cutting.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/sheetlaser.png",
          specifications: [
            { label: "Machine Make", value: "PRIDELASER" },
            { label: "Working Area", value: "6000*2000 MM" },
            { label: "Machine Type", value: "Fiber Laser" },
            { label: "Gas Used", value: "Nitrogen, Oxygen" },
            { label: "MS Capacity", value: "Up to 25MM" },
            { label: "SS Capacity", value: "Up to 16MM" },
            { label: "Copper Capacity", value: "Up to 3MM" }
          ],
          capabilities: [
            "High-speed cutting with minimal heat distortion",
            "Complex geometries and intricate patterns",
            "Nested cutting for material efficiency",
            "Automatic nozzle changing system"
          ],
          applications: ["Automotive components", "Industrial enclosures", "Architectural panels", "Precision parts", "Custom metal fabrication"]
        }
      ],
      processAdvantages: [
        { icon: <Zap />, title: "Speed", description: "Fast cutting speeds reduce production time significantly" },
        { icon: <Award />, title: "Precision", description: "Accuracy within ±0.05mm for consistent quality" },
        { icon: <Cog />, title: "Flexibility", description: "Handle various materials and complex geometries" },
        { icon: <Shield />, title: "Quality", description: "Clean edges with minimal post-processing" }
      ]
    },
    'pipe-laser-cutting': {
      id: 2,
      title: "Pipe Laser Cutting",
      icon: <Zap className="w-12 h-12" />,
      description: "Advanced laser cutting for pipes, tubes, and structural profiles with high accuracy. Round, Square, Rectangle, L Angle & C Channel profiles supported.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/MVI_2544.00_00_33_05.Still012.png",
      color: "bg-primary",
      subServices: [
        {
          name: "Laser Pipe Cutting",
          description: "Advanced laser cutting for pipes, tubes, and structural profiles with high accuracy.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/MVI_2544.00_00_33_05.Still012.png",
          specifications: [
            { label: "Pipe Diameter", value: "20mm - 240mm" },
            { label: "Max Length", value: "6000mm" },
            { label: "Wall Thickness", value: "0.5mm - 12mm" },
            { label: "Profile Types", value: "Round, Square, Rectangle, L Angle, C channel" },
            { label: "Cutting Angle", value: "0° - 45° beveling" },
            { label: "Precision", value: "±0.1mm" }
          ],
          capabilities: [
            "3D cutting with rotary axis",
            "Complex profile cutting",
            "Intersection cutting",
            "Automatic loading/unloading"
          ],
          applications: ["Structural frameworks", "Automotive exhaust systems", "Furniture components", "HVAC systems"]
        }
      ],
      processAdvantages: [
        { icon: <Zap />, title: "Speed", description: "Fast cutting speeds reduce production time significantly" },
        { icon: <Award />, title: "Precision", description: "Accuracy within ±0.1mm for consistent quality" },
        { icon: <Cog />, title: "Versatility", description: "Handle round, square, rectangular and custom profiles" },
        { icon: <Shield />, title: "Quality", description: "Clean edges with minimal post-processing" }
      ]
    },
    'cnc-bending': {
      id: 3,
      title: "CNC Bending",
      icon: <Settings className="w-12 h-12" />,
      description: "OPTIMUM CNC Bending Machine with 200 TON pressing force and 4300 MM bending length. Fully Servo with DELEM Da58 T controller for precise sheet bending operations.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/CNC/MVI_2544.00_00_51_26.Still017.png",
      color: "bg-primary",
      subServices: [
        {
          name: "CNC Sheet Bending",
          description: "Precision CNC bending for flat metal sheets with advanced servo control and DELEM controller.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/CNC/MVI_2544.00_00_51_26.Still017.png",
          specifications: [
            { label: "Machine Make", value: "OPTIMUM" },
            { label: "Pressing Force", value: "200 TON" },
            { label: "Bending Length", value: "4300 MM" },
            { label: "Bending Thickness", value: "Up to 8MM" },
            { label: "Machine Type", value: "Fully Servo" },
            { label: "Controller", value: "DELEM Da58 T" }
          ],
          capabilities: [
            "CNC-controlled back gauge for precision",
            "Automatic tool changing system",
            "3D simulation software",
            "Multi-bend sequencing for complex parts"
          ],
          applications: ["Enclosures & cabinets", "Brackets & supports", "Panels & covers", "Custom sheet metal parts"]
        }
      ],
      processAdvantages: [
        { icon: <Settings />, title: "Precision", description: "CNC control ensures repeatable accuracy every time" },
        { icon: <Cog />, title: "Servo Control", description: "Fully servo-driven for consistent bending results" },
        { icon: <Clock />, title: "Efficiency", description: "Fast setup and changeover for production flexibility" },
        { icon: <Award />, title: "Quality", description: "Consistent results with minimal material distortion" }
      ]
    },
    'rolling': {
      id: 4,
      title: "Rolling",
      icon: <Cog className="w-12 h-12" />,
      description: "Sheet and plate rolling services for cylindrical and conical shapes. Precision rolling for tanks, ducts, structural columns & custom curved components.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/image.png",
      color: "bg-primary",
      subServices: [
        {
          name: "Rolling",
          description: "Sheet and plate rolling services for cylindrical and conical shapes.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/image.png",
          specifications: [
            { label: "Max Width", value: "2500mm" },
            { label: "Max Thickness", value: "12mm MS" },
            { label: "Min Diameter", value: "200mm" },
            { label: "Shape Types", value: "Cylindrical, Conical" },
            { label: "Material Types", value: "MS, SS, Aluminum" }
          ],
          capabilities: [
            "Plate rolling for large cylinders",
            "Cone rolling with variable radius",
            "Pre-bending for complete circles",
            "Segment rolling for large diameters"
          ],
          applications: ["Tanks & vessels", "Ducts & chutes", "Structural columns", "Decorative elements"]
        }
      ],
      processAdvantages: [
        { icon: <Cog />, title: "Capability", description: "Handle large plates up to 2500mm width" },
        { icon: <Settings />, title: "Versatility", description: "Cylindrical and conical shapes with precision" },
        { icon: <Clock />, title: "Efficiency", description: "Pre-bending for complete circle forming" },
        { icon: <Award />, title: "Quality", description: "Consistent curvature with minimal distortion" }
      ]
    },
    'laser-welding': {
      id: 5,
      title: "Laser Welding",
      icon: <Wrench className="w-12 h-12" />,
      description: "State-of-the-art laser welding for strong, clean, and precise joints with minimal heat affected zone. Continuous & spot welding for SS, MS, and Aluminum.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/LaserWelding/welding.jpeg",
      color: "bg-primary",
      subServices: [
        {
          name: "Laser Welding",
          description: "State-of-the-art laser welding for strong, clean, and precise joints with minimal heat affected zone.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/LaserWelding/welding.jpeg",
          specifications: [
            { label: "Weld Type", value: "Continuous & Spot" },
            { label: "Penetration", value: "Up to 8mm" },
            { label: "Speed", value: "High-speed welding" },
            { label: "Heat Zone", value: "Minimal HAZ" },
            { label: "Materials", value: "SS, MS, Aluminum, Copper, Bronze" }
          ],
          capabilities: [
            "Precision micro-welding",
            "Minimal distortion on thin materials",
            "High-speed automated welding",
            "Clean welds requiring no finishing"
          ],
          applications: ["Medical devices", "Electronics enclosures", "Precision instruments", "Thin gauge assemblies"]
        }
      ],
      processAdvantages: [
        { icon: <Zap />, title: "Precision", description: "Micro-welding with minimal heat affected zone" },
        { icon: <Award />, title: "Quality", description: "Clean welds requiring no secondary finishing" },
        { icon: <Cog />, title: "Speed", description: "High-speed automated welding for production" },
        { icon: <Shield />, title: "Versatility", description: "Continuous and spot welding on multiple materials" }
      ]
    },
    'tig-welding': {
      id: 6,
      title: "TIG Welding",
      icon: <Wrench className="w-12 h-12" />,
      description: "Tungsten Inert Gas welding for superior quality and precision on critical applications. AOTAI Inverter Machine with 250-400 AMPS capacity.",
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1600&q=80",
      color: "bg-primary",
      subServices: [
        {
          name: "TIG Welding",
          description: "Tungsten Inert Gas welding for superior quality and precision on critical applications.",
          image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1600&q=80",
          specifications: [
            { label: "Machine Make", value: "AOTAI" },
            { label: "Capacity", value: "250-400 AMPS" },
            { label: "Machine Type", value: "Inverter" },
            { label: "Process", value: "TIG/GTAW" },
            { label: "Materials", value: "SS, Aluminum, Thin Gauge" }
          ],
          capabilities: [
            "Precision welding for thin materials",
            "Clean welds with minimal spatter",
            "AC/DC capability for various metals",
            "Pulse welding for heat control"
          ],
          applications: ["Stainless steel fabrication", "Aluminum welding", "Food grade equipment", "Aerospace components"]
        }
      ],
      processAdvantages: [
        { icon: <Zap />, title: "Precision", description: "Superior control for thin and delicate materials" },
        { icon: <Award />, title: "Quality", description: "Cleanest welds with minimal spatter" },
        { icon: <Cog />, title: "Versatility", description: "AC/DC capability for SS, Aluminum & more" },
        { icon: <Shield />, title: "Control", description: "Pulse welding for precise heat management" }
      ]
    },
    'mig-welding': {
      id: 7,
      title: "MIG Welding",
      icon: <Wrench className="w-12 h-12" />,
      description: "Metal Inert Gas welding for high-speed and efficient fabrication on production runs. AOTAI Inverter Machine with 250-400 AMPS capacity.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Tig&Mig/MVI_2544.00_00_16_11.Still004.png",
      color: "bg-primary",
      subServices: [
        {
          name: "MIG Welding",
          description: "Metal Inert Gas welding for high-speed and efficient fabrication on production runs.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Tig&Mig/Mig-Welding.jpeg",
          specifications: [
            { label: "Machine Make", value: "AOTAI" },
            { label: "Capacity", value: "250-400 AMPS" },
            { label: "Machine Type", value: "Inverter" },
            { label: "Process", value: "MIG/GMAW" },
            { label: "Materials", value: "Steel, Stainless, Aluminum" }
          ],
          capabilities: [
            "High-speed welding for production",
            "Inverter technology for stable arc",
            "Multiple material compatibility",
            "Adjustable wire feed speed"
          ],
          applications: ["Structural steel fabrication", "Heavy equipment", "Production welding", "Automotive fabrication"]
        }
      ],
      processAdvantages: [
        { icon: <Zap />, title: "Speed", description: "High-speed welding for large production runs" },
        { icon: <Award />, title: "Power", description: "250-400 AMPS capacity for thick materials" },
        { icon: <Cog />, title: "Efficiency", description: "Inverter technology for stable, consistent arc" },
        { icon: <Shield />, title: "Reliability", description: "Multiple material compatibility for any project" }
      ]
    },
    'buffing': {
      id: 8,
      title: "Buffing",
      icon: <Shield className="w-12 h-12" />,
      description: "Mirror finish buffing and polishing services. Professional surface finishing for stainless steel, aluminum & other metals to achieve premium aesthetics.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/buffing/polish-wheel-1-500x500.webp",
      color: "bg-primary",
      subServices: [
        {
          name: "Buffing & Polishing",
          description: "Mirror finish buffing and polishing services for stainless steel, aluminum & other metals.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/buffing/polish-wheel-1-500x500.webp",
          specifications: [
            { label: "Finish Types", value: "Mirror, Satin, Brushed" },
            { label: "Materials", value: "SS, Aluminum, Brass, Copper" },
            { label: "Surface Roughness", value: "Up to Ra 0.05μm" },
            { label: "Max Part Size", value: "Custom sizes" },
            { label: "Grit Range", value: "80 - 2000 grit" }
          ],
          capabilities: [
            "Mirror finish polishing",
            "Satin & brushed finishes",
            "Scratch removal and restoration",
            "Consistent finish quality across batches"
          ],
          applications: ["Stainless steel fabrication", "Decorative panels", "Food grade equipment", "Architectural elements"]
        }
      ],
      processAdvantages: [
        { icon: <Shield />, title: "Finish Quality", description: "Achieve mirror-grade surface finish" },
        { icon: <Award />, title: "Aesthetics", description: "Premium visual appeal for any metal surface" },
        { icon: <Cog />, title: "Versatility", description: "Multiple finish options - mirror, satin, brushed" },
        { icon: <Clock />, title: "Efficiency", description: "Quick turnaround on polishing services" }
      ]
    },
    'riveting': {
      id: 9,
      title: "Riveting",
      icon: <Shield className="w-12 h-12" />,
      description: "Professional stud and standoff riveting services. Secure fastening solutions for sheet metal assemblies, enclosures & industrial components with precision PEM hardware installation.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/riveting/af6f7f92ff35.jpeg",
      color: "bg-primary",
      subServices: [
        {
          name: "Riveting & Fastener Installation",
          description: "Professional stud riveting, standoff installation and PEM fastener services for sheet metal assemblies and enclosures.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/riveting/af6f7f92ff35.jpeg",
          specifications: [
            { label: "Fastener Types", value: "Studs, Standoffs, PEM Nuts, PEM Studs" },
            { label: "Materials", value: "SS, MS, Aluminum, Brass" },
            { label: "Sheet Thickness", value: "0.5mm - 6mm" },
            { label: "Stud Sizes", value: "M2 - M10" },
            { label: "Installation Force", value: "Up to 50 kN" }
          ],
          capabilities: [
            "Precision stud riveting for secure fastening",
            "Standoff installation for PCB & panel mounting",
            "PEM fastener press-fit installation",
            "Flush & protruding fastener options"
          ],
          applications: ["Sheet metal enclosures", "Electrical panels", "Electronic housings", "Industrial cabinets"]
        }
      ],
      processAdvantages: [
        { icon: <Shield />, title: "Strength", description: "High pull-out and torque-out resistance" },
        { icon: <Award />, title: "Precision", description: "Accurate placement with repeatable results" },
        { icon: <Cog />, title: "Versatility", description: "Multiple fastener types for any application" },
        { icon: <Clock />, title: "Speed", description: "Quick installation with minimal setup time" }
      ]
    },
    'drilling-tapping': {
      id: 10,
      title: "Drilling & Tapping",
      icon: <Cog className="w-12 h-12" />,
      description: "Precision drilling and tapping services for threaded holes. CNC drilling for accurate hole placement and consistent threading in SS, MS & Aluminum.",
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Drilling/MVI_2544.00_00_24_08.Still007.png",
      color: "bg-primary",
      subServices: [
        {
          name: "Drilling & Tapping",
          description: "CNC drilling and thread tapping services for precise hole placement and consistent threading in sheet metal and structural components.",
          image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Drilling/MVI_2544.00_00_24_08.Still007.png",
          specifications: [
            { label: "Drill Sizes", value: "M2 - M24" },
            { label: "Materials", value: "SS, MS, Aluminum, Brass" },
            { label: "Hole Tolerance", value: "±0.05mm" },
            { label: "Thread Types", value: "Metric, BSP, UNC, UNF" },
            { label: "Max Sheet Thickness", value: "Up to 25mm" }
          ],
          capabilities: [
            "CNC precision drilling for accurate hole placement",
            "Thread tapping for internal threads",
            "Counter boring & countersinking",
            "Multi-hole pattern drilling with consistent spacing"
          ],
          applications: ["Sheet metal assemblies", "Structural components", "Machine parts", "Enclosures & panels"]
        }
      ],
      processAdvantages: [
        { icon: <Shield />, title: "Accuracy", description: "CNC-controlled positioning for precise hole placement" },
        { icon: <Award />, title: "Consistency", description: "Repeatable results across large production batches" },
        { icon: <Cog />, title: "Versatility", description: "Multiple thread types and drill sizes available" },
        { icon: <Clock />, title: "Efficiency", description: "Fast turnaround with automated CNC operations" }
      ]
    }
  };

  const service = servicesData[serviceId];

  // Parallax scroll effects
  useEffect(() => {
    if (!service) return;

    // Hero section parallax
    if (heroRef.current) {
      const heroImage = heroRef.current.querySelector('.hero-bg-image');
      const heroOverlay = heroRef.current.querySelector('.hero-overlay');
      const heroContent = heroRef.current.querySelector('.hero-content');

      // Background image parallax
      if (heroImage) {
        gsap.to(heroImage, {
          yPercent: 30,
          scale: 1.2,
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          }
        });
      }

      // Overlay fade
      if (heroOverlay) {
        gsap.to(heroOverlay, {
          opacity: 0.6,
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          }
        });
      }

      // Content parallax
      if (heroContent) {
        gsap.to(heroContent, {
          yPercent: 50,
          opacity: 0.3,
          scale: 0.95,
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1.5,
          }
        });
      }
    }

    // Sub-services cards parallax
    if (subServicesRef.current) {
      const cards = subServicesRef.current.querySelectorAll('.sub-service-card');

      cards.forEach((card, index) => {
        // Entry animation
        gsap.fromTo(card,
          {
            opacity: 0,
            y: 100,
            scale: 0.9,
            rotateY: -15
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotateY: 0,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.5,
            }
          }
        );

        // Continuous parallax
        const parallaxSpeed = index % 2 === 0 ? -25 : -40;
        gsap.to(card, {
          y: parallaxSpeed,
          ease: "none",
          scrollTrigger: {
            trigger: subServicesRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 2,
          }
        });
      });

      // Section header parallax
      const header = subServicesRef.current.querySelector('.section-header');
      if (header) {
        gsap.to(header, {
          y: -50,
          opacity: 0.8,
          ease: "none",
          scrollTrigger: {
            trigger: subServicesRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
          }
        });
      }
    }

    // CTA section parallax
    if (ctaRef.current) {
      const ctaContent = ctaRef.current.querySelector('.cta-content');

      if (ctaContent) {
        gsap.fromTo(ctaContent,
          {
            opacity: 0,
            y: 80,
            scale: 0.9
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ctaRef.current,
              start: "top 80%",
              end: "top 40%",
              scrub: 1.5,
            }
          }
        );
      }
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-steel mb-4">Service Not Found</h1>
          <Link to="/services" className="text-primary hover:underline inline-flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Header */}
      <section ref={heroRef} className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        {/* Full Background Image */}
        <div className="hero-bg-image absolute inset-0">
          <img 
            src={service.image} 
            alt={service.title} 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Gradient Overlay */}
        <div className={`hero-overlay absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70`}></div>
        <div className={`absolute inset-0 ${service.color} opacity-30`}></div>

        <div className="hero-content relative z-10 max-w-7xl mx-auto px-6 w-full">
          {/* Breadcrumb */}
          <nav className="absolute top-24 left-6 right-6">
            <ol className="flex items-center space-x-2 text-white/80 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
              <li>/</li>
              <li className="text-white font-semibold">{service.title}</li>
            </ol>
          </nav>

          <div className="text-white text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block bg-white/20 backdrop-blur-sm p-6 rounded-3xl mb-6"
            >
              {service.icon}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-7xl font-bold mb-6"
            >
              {service.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl max-w-3xl mx-auto text-white/90"
            >
              {service.description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Process Advantages Section */}
      {service.processAdvantages && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-steel mb-4">Why Choose Our {service.title}?</h2>
              <p className="text-steel-lighter max-w-2xl mx-auto">
                Industry-leading capabilities that deliver superior results
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.processAdvantages.map((advantage, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300 group"
                >
                  <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                    {advantage.icon}
                  </div>
                  <h3 className="text-xl font-bold text-steel mb-2">{advantage.title}</h3>
                  <p className="text-steel-lighter text-sm">{advantage.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sub-services Section with Detailed Specifications */}
      <section ref={subServicesRef} className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="section-header text-center mb-16">
            <h2 className="text-4xl font-bold text-steel mb-4">Detailed Service Offerings</h2>
            <p className="text-xl text-steel-lighter max-w-2xl mx-auto">
              Comprehensive technical specifications and capabilities for each service
            </p>
          </div>

          <div className="space-y-12">
            {service.subServices.map((subService, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: idx * 0.1 }}
                className="sub-service-card bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100"
              >
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Image Section */}
                  <div className="relative h-80 md:h-auto overflow-hidden">
                    <img
                      src={subService.image}
                      alt={subService.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute bottom-6 left-6 right-6 bg-black/60 backdrop-blur-sm p-4 rounded-lg">
                      <h3 className="text-3xl font-bold text-white mb-2">{subService.name}</h3>
                      <p className="text-white/90">{subService.description}</p>
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="p-8">
                    {/* Technical Specifications */}
                    {subService.specifications && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-steel mb-4 flex items-center gap-2">
                          <div className={`w-1 h-6 ${service.color} rounded-full`}></div>
                          Technical Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {subService.specifications.map((spec, specIdx) => (
                            <div key={specIdx} className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm text-steel-lighter mb-1">{spec.label}</div>
                              <div className="font-semibold text-steel">{spec.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Capabilities */}
                    {subService.capabilities && (
                      <div className="mb-8">
                        <h4 className="text-xl font-bold text-steel mb-4 flex items-center gap-2">
                          <div className={`w-1 h-6 ${service.color} rounded-full`}></div>
                          Key Capabilities
                        </h4>
                        <div className="grid gap-2">
                          {subService.capabilities.map((capability, capIdx) => (
                            <div key={capIdx} className="flex items-start gap-3">
                              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                              <span className="text-steel-lighter">{capability}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Applications */}
                    {subService.applications && (
                      <div>
                        <h4 className="text-xl font-bold text-steel mb-4 flex items-center gap-2">
                          <div className={`w-1 h-6 ${service.color} rounded-full`}></div>
                          Common Applications
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {subService.applications.map((app, appIdx) => (
                            <span
                              key={appIdx}
                              className={`px-4 py-2 ${service.color} text-white rounded-full text-sm font-medium`}
                            >
                              {app}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Old Simple Cards Section - Removed */}

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-6 bg-white">
        <div className="cta-content max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-steel mb-6">
            Ready to Get Started with {service.title}?
          </h2>
          <p className="text-xl text-steel-lighter mb-8">
            Contact us today for a free consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-gradient-to-r from-primary to-secondary text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              Get a Quote <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/services"
              className="bg-steel text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" /> View All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
